import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader, Card, StatusBadge } from "@/components/ui-helpers";
import { CalendarRange, Plus, X, Lock, Unlock, Check } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";

export default function FinancialYearPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  function load() {
    api.get("/financial-years").then(r => setList(r.data || []));
  }
  useEffect(() => { load(); }, []);

  async function createFY(e) {
    e.preventDefault();
    if (!code.match(/^\d{2}-\d{2}$/)) {
      toast.error("Code must look like 25-26");
      return;
    }
    setBusy(true);
    try {
      await api.post("/financial-years", { code });
      toast.success(`Created FY ${code}`);
      setCode("");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function switchTo(c) {
    try {
      await api.post(`/financial-years/switch/${c}`);
      toast.success(`Active FY → ${c}`);
      load();
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Switch failed");
    }
  }

  async function closeFY(c) {
    if (!window.confirm(`Close FY ${c}? No new entries will be allowed in this FY until you re-open it.`)) return;
    try {
      await api.post(`/financial-years/${c}/close`);
      toast.success(`FY ${c} closed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    }
  }

  async function reopenFY(c) {
    try {
      await api.post(`/financial-years/${c}/reopen`);
      toast.success(`FY ${c} re-opened`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-open failed");
    }
  }

  return (
    <div data-testid="fy-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Administration"
        title="Financial Year"
        subtitle="Indian financial year runs Apr 1 → Mar 31. The Active FY scopes every new transaction. To record back-dated entries, switch the Active FY first."
        actions={
          <button data-testid="add-fy-btn" onClick={() => setOpen(true)} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> Add Financial Year
          </button>
        }
      />

      <Card className="overflow-hidden">
        <table className="w-full" data-testid="fy-table">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              {["FY Code", "Start", "End", "Status", "Active", "Closed By", "Actions"].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {list.map(fy => (
              <tr key={fy.code} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <CalendarRange size={14} className="text-[#F97316]" />
                    <span className="font-bold text-sm text-slate-900">FY {fy.code}</span>
                  </div>
                </td>
                <td className="py-4 px-4 text-sm text-slate-700">{formatDate(fy.start_date)}</td>
                <td className="py-4 px-4 text-sm text-slate-700">{formatDate(fy.end_date)}</td>
                <td className="py-4 px-4"><StatusBadge status={fy.status} /></td>
                <td className="py-4 px-4">
                  {fy.is_active ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                      <Check size={14} /> Active
                    </span>
                  ) : (
                    <button data-testid={`set-active-${fy.code}`} onClick={() => switchTo(fy.code)} className="text-[10px] font-bold uppercase tracking-wider border border-slate-300 text-slate-600 px-2 py-1 hover:border-[#F97316] hover:text-[#F97316]">
                      Set Active
                    </button>
                  )}
                </td>
                <td className="py-4 px-4 text-xs text-slate-500">{fy.closed_by || "—"}</td>
                <td className="py-4 px-4">
                  {fy.status === "Open" ? (
                    <button data-testid={`close-fy-${fy.code}`} onClick={() => closeFY(fy.code)} className="text-[10px] font-bold uppercase tracking-wider text-red-600 hover:underline flex items-center gap-1">
                      <Lock size={11} /> Close FY
                    </button>
                  ) : (
                    <button data-testid={`reopen-fy-${fy.code}`} onClick={() => reopenFY(fy.code)} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:underline flex items-center gap-1">
                      <Unlock size={11} /> Re-open
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="mt-6 bg-orange-50 border border-orange-200 px-5 py-4 text-xs text-slate-700 rounded-sm">
        <strong className="text-orange-900">How this works:</strong>
        <ul className="mt-2 space-y-1 list-disc pl-5">
          <li>One financial year is <strong>Active</strong> at a time. New invoices / bills / vouchers / payments are all stamped with the active FY and their document date must fall within FY bounds.</li>
          <li>For back-dated entries in an older FY, switch the Active FY using the topbar selector or &quot;Set Active&quot; button above.</li>
          <li>The next FY is auto-created within 60 days of the current FY ending.</li>
          <li>Closing an FY blocks all new entries; re-open it from this page to record more entries later.</li>
        </ul>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">Add Financial Year</h3>
              <button onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <form onSubmit={createFY} className="p-5 space-y-4">
              <Field label="FY Code">
                <input data-testid="new-fy-code" autoFocus value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. 24-25" className={inputCls} />
              </Field>
              <p className="text-[11px] text-slate-500">Start/end dates auto-computed: Apr 1 → Mar 31</p>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase">Cancel</button>
                <button data-testid="save-fy-btn" disabled={busy} type="submit" className="px-4 py-2 bg-[#F97316] text-white text-xs font-bold uppercase">
                  {busy ? "Saving…" : "Create FY"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
